import { useCallback, useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import { Ltr, useTranslation } from '../i18n.js'
import { signUp, type SignUpResult } from '../session/session.js'

/**
 * Signing a shop up. `OPS-201`, `OPS-210`, `TEN-002a`.
 *
 * **The product had no way in.** `SignupService` had existed for a while with no controller and no
 * screen, so a shop could create an account nowhere: every path into Lensmira started with a
 * database somebody had provisioned by hand. `later_todo.md` 61.
 *
 * Three decisions here are judgement rather than specification, so they are written down.
 *
 * **The country is a choice, not a constant.** v1 is Pakistan-only and `SUPPORTED_COUNTRIES` is
 * `['PK']`, so the obvious thing is to hardcode it. That would make `OPS-210` unreachable: it is a
 * `MUST` that a signup from an unsupported country "reaches a waitlist with a clear explanation",
 * and a form that cannot express an unsupported country can never produce one. The list is short
 * and is the markets a Pakistani optician product plausibly hears from, not a claim about where it
 * will ship.
 *
 * **Success is not a sign-in.** `TEN-002a` puts provisioning in a background job, so when this
 * returns the shop has a directory row and no database yet. Showing a signed-in screen would be a
 * lie for the few seconds that takes, and a failed sign-in afterwards would read as a bad
 * password. So the done state says what is happening and sends them to sign in themselves.
 *
 * **The done state is where the authenticator is explained.** `TEN-005` makes TOTP mandatory for
 * an owner, and `auth.service.ts` deliberately cannot tell a missing authenticator from a wrong
 * password, so the first sign-in of every new shop is refused with "that email address and
 * password don't match" unless somebody has told them otherwise. This is the one moment in the
 * product where we know for certain that they have not enrolled, so it is the honest place to say
 * so.
 */

export interface SignUpProps {
  readonly baseUrl: string
  readonly doFetch: typeof globalThis.fetch
  /** Back to the sign-in screen, which is the only other thing an unauthenticated person can do. */
  readonly onBack: () => void
}

/**
 * Enough countries that `OPS-210`'s waitlist is reachable from the form.
 *
 * Two-letter ISO codes, because `signup_waitlist_country_is_iso` is a check constraint and a free
 * text field would reach it as a 500 rather than a refusal.
 *
 * **The label is a written-out key, not `signUp.country.${code}`.** Appendix S wants the catalogue
 * reached by key, and `i18n-static.test.ts` enforces it by scanning the source for quoted key
 * literals: a key built from a loop variable is invisible to it, so all eight would have been
 * reported as English nobody uses and deleted by whoever tidied up next.
 */
const COUNTRIES = [
  { code: 'PK', label: 'signUp.country.PK' },
  { code: 'IN', label: 'signUp.country.IN' },
  { code: 'BD', label: 'signUp.country.BD' },
  { code: 'LK', label: 'signUp.country.LK' },
  { code: 'AE', label: 'signUp.country.AE' },
  { code: 'SA', label: 'signUp.country.SA' },
  { code: 'GB', label: 'signUp.country.GB' },
  { code: 'US', label: 'signUp.country.US' },
] as const

export function SignUp({ baseUrl, doFetch, onBack }: SignUpProps): JSX.Element {
  const { t } = useTranslation()
  const [shopName, setShopName] = useState('')
  const [ownerName, setOwnerName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [countryCode, setCountryCode] = useState<string>('PK')
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<SignUpResult | null>(null)
  const first = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // `KB-02`: reachable without a mouse, starting with not needing one to begin.
    first.current?.focus()
  }, [])

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      setBusy(true)
      setMessage(null)
      const result = await signUp(
        baseUrl,
        { countryCode, email, shopName, ownerName, password },
        doFetch,
      )
      setBusy(false)

      switch (result.kind) {
        case 'provisioning':
        case 'waitlisted':
          setDone(result)
          return
        case 'unavailable':
          // A dead network is this client's own sentence, so it is said in the shop's language.
          setMessage(result.reason === 'network' ? t('signUp.unreachable') : result.message)
          return
        case 'rejected':
          // The server's words: it knows whether the address is taken or the name is blank, and
          // repeating that judgement here would be a second copy of it that drifts.
          setMessage(result.message)
      }
    },
    [baseUrl, countryCode, doFetch, email, ownerName, password, shopName, t],
  )

  if (done?.kind === 'waitlisted') {
    return (
      <section className="lm-screen lm-screen--narrow" aria-labelledby="sign-up-heading">
        <h1 id="sign-up-heading" className="lm-screen__heading">
          {t('signUp.waitlistHeading')}
        </h1>
        {/* `OPS-210`: "a clear explanation", which means saying what happens next rather than
            only that it did not work. */}
        <p className="lm-hint" role="status">
          {t('signUp.waitlistMeans')}
        </p>
        <button type="button" className="lm-button" onClick={onBack}>
          {t('signUp.backToSignIn')}
        </button>
      </section>
    )
  }

  if (done?.kind === 'provisioning') {
    return (
      <section className="lm-screen lm-screen--narrow" aria-labelledby="sign-up-heading">
        <h1 id="sign-up-heading" className="lm-screen__heading">
          {t('signUp.doneHeading')}
        </h1>
        <p className="lm-hint" role="status">
          {t('signUp.doneMeans')}
        </p>

        <span className="lm-field__label">{t('signUp.doneSignInAs')}</span>
        {/* `LOC-005`: an address is Latin whatever the page direction, and it must not mirror. */}
        <code className="lm-enrol__secret" dir="ltr">
          <Ltr>{done.signInAs}</Ltr>
        </code>

        {/* The one moment we know for certain they have not enrolled. `TEN-005`. */}
        <h2 className="lm-enrol__heading">{t('signUp.authenticatorHeading')}</h2>
        <p className="lm-enrol__means">{t('signUp.authenticatorMeans')}</p>

        <button type="button" className="lm-button lm-button--primary" onClick={onBack}>
          {t('signUp.backToSignIn')}
        </button>
      </section>
    )
  }

  return (
    <section className="lm-screen lm-screen--narrow" aria-labelledby="sign-up-heading">
      <h1 id="sign-up-heading" className="lm-screen__heading">
        {t('signUp.heading')}
      </h1>
      <p className="lm-enrol__means">{t('signUp.means')}</p>

      <form
        onSubmit={(e) => {
          void submit(e)
        }}
        className="lm-form"
      >
        <label className="lm-field">
          <span className="lm-field__label">{t('signUp.shopName')}</span>
          <input
            ref={first}
            className="lm-input"
            autoComplete="organization"
            value={shopName}
            onChange={(e) => setShopName(e.currentTarget.value)}
          />
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('signUp.ownerName')}</span>
          <input
            className="lm-input"
            autoComplete="name"
            value={ownerName}
            onChange={(e) => setOwnerName(e.currentTarget.value)}
          />
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('signUp.email')}</span>
          <input
            className="lm-input"
            dir="ltr"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('signUp.password')}</span>
          <input
            className="lm-input"
            dir="ltr"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
          />
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('signUp.country')}</span>
          <select
            className="lm-input"
            value={countryCode}
            onChange={(e) => setCountryCode(e.currentTarget.value)}
          >
            {COUNTRIES.map((country) => (
              <option key={country.code} value={country.code}>
                {t(country.label)}
              </option>
            ))}
          </select>
        </label>

        <button type="submit" className="lm-button lm-button--primary" disabled={busy}>
          {busy ? t('signUp.busy') : t('signUp.submit')}
        </button>
      </form>

      <button type="button" className="lm-button" onClick={onBack}>
        {t('signUp.haveAnAccount')}
      </button>

      {message ? (
        <p className="lm-hint" role="alert">
          {message}
        </p>
      ) : null}
    </section>
  )
}
